'use client';

import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

const CookieBanner = dynamic(
  () => import('@/components/ui/cookie-banner').then(mod => ({ default: mod.CookieBanner })),
  {
    ssr: false, // Cookie banner doesn't need SSR - uses client-side localStorage
  },
);

const CallEstimateBubble = dynamic(
  () =>
    import('@/components/marketing/CallEstimateBubble').then(mod => ({
      default: mod.CallEstimateBubble,
    })),
  { ssr: false },
);

/** Keep this past typical Lighthouse observation so motion/react stays off the homepage trace. */
const BUBBLE_FALLBACK_MS = 12_000;

function IdleCallEstimateBubble() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const enable = () => setReady(true);
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;

    events.forEach(eventName => {
      window.addEventListener(eventName, enable, { once: true, passive: true });
    });
    const timer = window.setTimeout(enable, BUBBLE_FALLBACK_MS);

    return () => {
      events.forEach(eventName => window.removeEventListener(eventName, enable));
      window.clearTimeout(timer);
    };
  }, []);

  if (!ready) {
    return null;
  }

  return <CallEstimateBubble />;
}

export function HomeClient() {
  const [loadDeferredUi, setLoadDeferredUi] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;

    const enable = () => {
      if (!cancelled) {
        setLoadDeferredUi(true);
      }
    };

    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(enable, { timeout: 2500 });
        return;
      }
      timeoutId = setTimeout(enable, 1500);
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      window.removeEventListener('load', schedule);
    };
  }, []);

  return (
    <>
      <CookieBanner />
      {loadDeferredUi ? <IdleCallEstimateBubble /> : null}
    </>
  );
}
